// --- React
import { useCallback, useMemo, useState } from "react";
// --- Config & Utils
import * as columnDefs from "../../../../../components/AGGrid/config/columns-def";
// --- Hooks
import { useRateCardsData } from "../../../hooks/useRateCardsData";

/**
 * Custom hook for Rate Cards grid utilities.
 *
 * Features:
 * - Manages sorting state and logic for the Rate Cards grid
 * - Fetches sorted rate cards data from the API via useWindowsData
 * - Prepares column definitions with sorting controls
 *
 * @param {object} params - Optional configuration
 * @param {Array} params.columnHandlers - Handlers to pass to column definitions
 * @param {boolean} params.isTapAdmin - Whether the current user has TAP Admin permissions
 * @returns {object} { setSort, columns }
 */
export function useRateCardGridUtils({
  columnHandlers = [],
  isTapAdmin = false,
} = {}) {
  const { fetchRateCards, updateRateCards, rateCardsRange } =
    useRateCardsData();
  const [sortedColId, setSortedColId] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  // Handle sorting: update state, fetch sorted data, update Rate Cards
  const setSort = useCallback(
    async (colId, direction) => {
      setSortedColId(colId);
      setSortDirection(direction);

      const customSort = [colId, direction.toUpperCase()];
      const data = await fetchRateCards(rateCardsRange, null, customSort);

      if (data) {
        updateRateCards({
          rateCards: data?.content,
          totalPages: data?.totalPages,
          totalItems: data?.totalElements,
          currentPage: data?.pageable?.pageNumber,
          rateCardsSort: customSort,
        });
      }
    },
    [fetchRateCards, updateRateCards, rateCardsRange],
  );

  // Prepare column definitions with sorting controls
  const baseColumns = columnDefs["rateCards"](...columnHandlers, isTapAdmin)[
    "default"
  ];

  const columns = useMemo(
    () =>
      baseColumns.map((col) => ({
        ...col,
        headerComponentParams: {
          ...col.headerComponentParams,
          setSort,
          sortedColId,
          sortDirection,
        },
      })),
    [setSort, sortedColId, sortDirection, baseColumns],
  );

  return { setSort, columns };
}
